import { useCallback, useEffect, useRef } from 'react';

import { TableRowSelection } from 'antd/es/table/interface';

import { includes, last } from 'ramda';
import { isNotNilOrEmpty } from 'ramda-adjunct';

import { useMergingState } from './useMergeState.hook';

export const useRowSelection = <T extends TableRowSelection<any>, U = any>(
	props?: T & {
		isSingle?: boolean;
	}
) => {
	const [state, setState] = useMergingState({
		selectedRowKeys: [] as React.Key[],
		selectedRows: [] as U[],
	});

	const onSelectChange = useCallback(
		(newSelectedRowKeys: React.Key[], newSelectedRows: U[]) => {
			if (props?.isSingle) {
				isNotNilOrEmpty(last(newSelectedRowKeys)) &&
				!includes(last(newSelectedRowKeys), state.selectedRowKeys)
					? setState({
							selectedRowKeys: [last(newSelectedRowKeys) as React.Key],
							selectedRows: [last(newSelectedRows) as U],
						})
					: setState({
							selectedRowKeys: [],
							selectedRows: [],
						});

				return;
			}

			setState({
				selectedRowKeys: newSelectedRowKeys,
				selectedRows: newSelectedRows,
			});
		},
		[state.selectedRowKeys]
	);

	const _onReset = useCallback(() => {
		setState({
			selectedRowKeys: [],
			selectedRows: [],
		});
	}, []);

	const hasMounted = useRef(false);

	useEffect(() => {
		if (props?.defaultSelectedRowKeys && !hasMounted.current) {
			setState({
				selectedRowKeys: props.defaultSelectedRowKeys,
			});
			hasMounted.current = true;
		}
	}, [props?.defaultSelectedRowKeys]);

	return {
		selectedRowKeys: state.selectedRowKeys,
		selectedRows: state.selectedRows,
		onChange: onSelectChange,
		onReset: _onReset,
		setState,
		preserveSelectedRowKeys: true,
		...props,
	};
};
